import { Component } from '@angular/core';
import { ExportEngine, type ExportColumn } from '@memberjunction/export-engine';
import { NeutralizeFormulaCells } from 'agu_app_shell';
import { OEPViewBase } from '../../state/oep-view-base';
import type { OEPStatusLine } from '../shared/status';
import { OEPDownloadExport } from '../shared/download';
import { ClockET } from '../shared/format';
import {
    ActivityTiles, DailyCsvColumns, DailyCsvRows, DailyRows, DefaultRole, OEP_ACTIVITY_ROLES, OEP_DAILY_ACTIVITY_QUERY_ID,
    OEP_PARTICIPANT_ACTIVITY_QUERY_ID, ParseDayRow, ParsePersonRow, PeopleCsvColumns, PeopleCsvRows, RoleTabs,
    type OEPActivityDay, type OEPActivityDayVM, type OEPActivityPerson, type OEPActivityRole,
    type OEPActivityTab, type OEPActivityTile, type OEPCsvRow,
} from './activity.vm';

const NOUN: Readonly<Record<OEPActivityRole, [string, string]>> = {
    Presenter: ['presenter', 'presenters'], Reviewer: ['reviewer', 'reviewers'],
    Nominator: ['nominator', 'nominators'], Committee: ['section committee member', 'section committee members'],
};
const FILE_ROLE: Readonly<Record<OEPActivityRole, string>> = {
    Presenter: 'presenters', Reviewer: 'reviewers', Nominator: 'nominators', Committee: 'committees',
};

/**
 * AGU staff "Activity": by OEP role, summary progress tiles (signed in, opened OEP, role progress, in OEP today),
 * daily activity for the last 14 US Eastern days, and CSV exports of both the role's people and daily counts.
 * Individual people lookup, filtering, and presenter edits live on User management.
 */
@Component({
    standalone: false,
    selector: 'oep-activity',
    templateUrl: './oep-activity.component.html',
    styleUrl: './oep-activity.component.scss',
})
export class OEPActivityComponent extends OEPViewBase {
    SeasonCode = '';
    AsOf = '';
    Tabs: OEPActivityTab[] = [];
    Role: OEPActivityRole = 'Presenter';
    Tiles: OEPActivityTile[] = [];
    Days: OEPActivityDayVM[] = [];
    /** The result of the last export, announced in the aria-live line. */
    Status: OEPStatusLine | null = null;

    private people: OEPActivityPerson[] = [];
    private days: OEPActivityDay[] = [];
    private roleChosen = false;
    private loadedAt = new Date();

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const [people, days] = await this.Data.Queries([OEP_PARTICIPANT_ACTIVITY_QUERY_ID, OEP_DAILY_ACTIVITY_QUERY_ID]);
        this.people = people.map(ParsePersonRow);
        this.days = days.map(ParseDayRow);
        this.loadedAt = new Date();
        this.SeasonCode = season.Code;
        this.AsOf = `Updated ${ClockET(this.loadedAt)}`;
        this.Tabs = RoleTabs(this.people);
        // A refresh keeps the role the staff member is looking at; only the first load picks one.
        if (!this.roleChosen) this.Role = DefaultRole(this.people);
        this.rebuildRole();
    }

    /** mj-tab-nav emits the tab key; anything that is not an OEP activity role is ignored. */
    SelectRole(key: string): void {
        const role = OEP_ACTIVITY_ROLES.find((r) => r === key);
        if (!role || role === this.Role) return;
        this.Role = role;
        this.roleChosen = true;
        this.rebuildRole();
        this.Changed();
    }

    /** "Export people": everyone in this role with their sign-in, OEP activity, and role progress columns. */
    async ExportPeople(): Promise<void> {
        const inRole = this.people.filter((p) => p.Role === this.Role);
        const count = inRole.length;
        const [one, many] = NOUN[this.Role];
        await this.export(
            PeopleCsvRows(this.Role, inRole),
            PeopleCsvColumns(this.Role),
            `OEP-${this.SeasonCode}-${FILE_ROLE[this.Role]}`,
            `Exported ${count} ${count === 1 ? one : many}.`,
        );
    }

    /** "Export by day": the 14-day table for this role. */
    async ExportDaily(): Promise<void> {
        await this.export(
            DailyCsvRows(this.Role, this.Days),
            DailyCsvColumns(this.Role),
            `OEP-${this.SeasonCode}-${FILE_ROLE[this.Role]}-by-day`,
            `Exported ${this.Days.length} days.`,
        );
    }

    private async export(rows: OEPCsvRow[], columnNames: readonly string[], name: string, done: string): Promise<void> {
        this.Status = null;
        const fileName = name.replace(/[\\/:*?"<>|]+/g, '-');
        try {
            const columns: ExportColumn[] = columnNames.map((c) => ({ name: c, displayName: c }));
            const result = await ExportEngine.toCSV(NeutralizeFormulaCells(rows), { fileName, columns });
            OEPDownloadExport(result, fileName, 'csv', 'Couldn\'t build the CSV.');
            this.Status = { Text: done, Tone: 'ok' };
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }

    private rebuildRole(): void {
        this.Tiles = ActivityTiles(this.Role, this.people, this.days, this.loadedAt);
        this.Days = DailyRows(this.Role, this.days, this.loadedAt);
    }
}
